import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border/40 mb-2" data-testid={`section-${title.slice(0,20)}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary/30 transition-colors">
        <h2 className="font-heading text-base font-bold text-white">{title}</h2>
        {open ? <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-foreground/80 leading-relaxed space-y-3">{children}</div>}
    </div>
  );
}

export default function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="terms-page">
      <SEO title="Regulamin" description="Regulamin serwisu Znajdownik.pl. Zasady korzystania z portalu, system Zorki, prawa i obowiązki użytkowników." />

      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">REGULAMIN SERWISU</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Znajdownik.pl – Łowca Okazji | Obowiązuje od marca 2026</p>

      <Section title="§1. Postanowienia ogólne" defaultOpen={true}>
        <p>1. Niniejszy Regulamin określa zasady korzystania z serwisu internetowego Znajdownik.pl (dalej: „Serwis"), prowadzonego pod adresem https://znajdownik.pl.</p>
        <p>2. Właścicielem i administratorem Serwisu jest jego operator (dalej: „Administrator").</p>
        <p>3. Serwis jest platformą społecznościową umożliwiającą użytkownikom dzielenie się okazjami zakupowymi, przeglądanie ofert produktów, korzystanie z artykułów blogowych, systemu walutowego Zorki oraz narzędzia AI „Wąchalski".</p>
        <p>4. Korzystanie z Serwisu oznacza akceptację niniejszego Regulaminu.</p>
      </Section>

      <Section title="§2. Definicje">
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Użytkownik</strong> – osoba korzystająca z Serwisu, zarejestrowana lub niezarejestrowana.</li>
          <li><strong>Konto</strong> – indywidualne konto Użytkownika w Serwisie, utworzone po rejestracji.</li>
          <li><strong>Okazja (Deal)</strong> – wpis dodany przez Użytkownika lub zaimportowany automatycznie, prezentujący ofertę zakupową.</li>
          <li><strong>Produkt</strong> – okazja wzbogacona o profesjonalny opis SEO, specyfikację i recenzję eksperta.</li>
          <li><strong>Zorki</strong> – wirtualna waluta serwisu, przyznawana za aktywność i wydawana w Sklepie Nagród.</li>
          <li><strong>Wąchalski</strong> – narzędzie AI do inteligentnego wyszukiwania okazji.</li>
          <li><strong>Gadulec</strong> – główny feed (tablica) z najnowszymi okazjami.</li>
        </ul>
      </Section>

      <Section title="§3. Rejestracja i konto">
        <p>1. Rejestracja w Serwisie jest bezpłatna i wymaga podania unikalnego nicku oraz hasła. Opcjonalnie można podać adres e-mail lub numer telefonu.</p>
        <p>2. Użytkownik zobowiązuje się do podania prawdziwych danych i nieudostępniania swojego konta osobom trzecim.</p>
        <p>3. Administrator zastrzega sobie prawo do zablokowania lub usunięcia konta naruszającego Regulamin.</p>
        <p>4. Użytkownik może w każdej chwili usunąć swoje konto kontaktując się z Administratorem.</p>
      </Section>

      <Section title="§4. Zasady korzystania z Serwisu">
        <p>1. Użytkownik zobowiązuje się do korzystania z Serwisu zgodnie z prawem, dobrymi obyczajami i niniejszym Regulaminem.</p>
        <p>2. Zabronione jest:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>publikowanie treści obraźliwych, wulgarnych, dyskryminujących lub niezgodnych z prawem,</li>
          <li>spam, reklama niezwiązana z okazjami, manipulacja systemem Zorki,</li>
          <li>podszywanie się pod innych użytkowników lub osoby publiczne,</li>
          <li>automatyczne zbieranie danych z Serwisu (scraping) bez zgody Administratora,</li>
          <li>działania mogące zakłócić funkcjonowanie Serwisu.</li>
        </ul>
        <p>3. Administrator zastrzega sobie prawo do usuwania treści naruszających Regulamin oraz moderowania wpisów.</p>
      </Section>

      <Section title="§5. System walutowy Zorki">
        <p>1. Zorki to wirtualna waluta Serwisu, niemająca wartości pieniężnej i niewymienialna na środki pieniężne.</p>
        <p>2. Zorki przyznawane są za aktywność:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>+100 Zorków – bonus powitalny przy rejestracji,</li>
          <li>+10 Zorków – dodanie okazji,</li>
          <li>+5 Zorków – napisanie recenzji produktu,</li>
          <li>+3 Zorków – post w grupie,</li>
          <li>+2 Zorków – komentarz,</li>
          <li>+1 do +10 Zorków – streak logowania (dni z rzędu),</li>
          <li>+50 Zorków – polecenie nowego użytkownika,</li>
          <li>Zorki za wyzwania dnia.</li>
        </ul>
        <p>3. Zorki można wydać w Sklepie Nagród na badge'e, wyróżnienia i bonusy.</p>
        <p>4. Administrator zastrzega sobie prawo do zmiany wartości przyznawanych Zorków.</p>
      </Section>

      <Section title="§6. Treści użytkowników">
        <p>1. Użytkownik ponosi pełną odpowiedzialność za treści publikowane w Serwisie (okazje, komentarze, recenzje, posty w grupach).</p>
        <p>2. Publikując treści, Użytkownik udziela Administratorowi niewyłącznej, nieodpłatnej licencji na ich wykorzystanie w ramach Serwisu.</p>
        <p>3. Administrator nie ponosi odpowiedzialności za treści publikowane przez Użytkowników, ale dokłada starań w zakresie ich moderacji.</p>
      </Section>

      <Section title="§7. Linki afiliacyjne i reklamy">
        <p>1. Serwis zawiera linki afiliacyjne prowadzące do zewnętrznych sklepów i sprzedawców. Kliknięcie w taki link i dokonanie zakupu może generować prowizję dla Serwisu.</p>
        <p>2. Ceny produktów prezentowanych w Serwisie mają charakter informacyjny i mogą różnić się od cen w sklepach docelowych.</p>
        <p>3. Serwis nie jest stroną umowy sprzedaży między Użytkownikiem a sprzedawcą zewnętrznym. Wszelkie reklamacje dotyczące zakupionych produktów należy kierować do sprzedawcy.</p>
        <p>4. W Serwisie mogą być wyświetlane reklamy za pośrednictwem Google AdSense i innych systemów reklamowych.</p>
      </Section>

      <Section title="§8. Narzędzie AI (Wąchalski)">
        <p>1. Wąchalski to narzędzie oparte na sztucznej inteligencji, służące do wyszukiwania okazji i generowania opisów produktów.</p>
        <p>2. Wyniki generowane przez AI mają charakter pomocniczy i nie stanowią porady zakupowej, inwestycyjnej ani eksperckiej.</p>
        <p>3. Administrator nie ponosi odpowiedzialności za dokładność i kompletność treści generowanych przez AI.</p>
      </Section>

      <Section title="§9. API dla podmiotów zewnętrznych (Epidemos)">
        <p>1. Serwis udostępnia API umożliwiające zewnętrznym systemom publikowanie okazji, komentarzy i ocen.</p>
        <p>2. Korzystanie z API wymaga klucza autoryzacyjnego (API Key) i odbywa się na warunkach uzgodnionych z Administratorem.</p>
        <p>3. Podmiot korzystający z API ponosi odpowiedzialność za publikowane treści.</p>
      </Section>

      <Section title="§10. Odpowiedzialność Administratora">
        <p>1. Administrator dokłada starań, aby Serwis działał prawidłowo i był dostępny nieprzerwanie, jednak nie gwarantuje ciągłości działania.</p>
        <p>2. Administrator nie ponosi odpowiedzialności za przerwy w działaniu Serwisu wynikające z konserwacji, awarii lub siły wyższej.</p>
        <p>3. Administrator nie ponosi odpowiedzialności za decyzje zakupowe Użytkowników podjęte na podstawie informacji z Serwisu.</p>
      </Section>

      <Section title="§11. Własność intelektualna">
        <p>1. Szata graficzna, logo, opisy produktów wygenerowane przez AI oraz inne elementy Serwisu stanowią własność Administratora lub są wykorzystywane na podstawie licencji.</p>
        <p>2. Kopiowanie, rozpowszechnianie lub wykorzystywanie treści Serwisu bez zgody Administratora jest zabronione.</p>
      </Section>

      <Section title="§12. Postępowanie reklamacyjne">
        <p>1. Reklamacje dotyczące funkcjonowania Serwisu należy kierować na adres: <a href="mailto:kontakt@znajdownik.pl" className="text-primary hover:underline">kontakt@znajdownik.pl</a>.</p>
        <p>2. Reklamacja powinna zawierać opis problemu, dane kontaktowe Użytkownika oraz oczekiwany sposób rozwiązania.</p>
        <p>3. Administrator rozpatrzy reklamację w terminie 14 dni roboczych.</p>
      </Section>

      <Section title="§13. Postanowienia końcowe">
        <p>1. Regulamin wchodzi w życie z dniem opublikowania w Serwisie.</p>
        <p>2. Administrator zastrzega sobie prawo do zmiany Regulaminu. O zmianach Użytkownicy zostaną poinformowani z wyprzedzeniem co najmniej 14 dni.</p>
        <p>3. W sprawach nieuregulowanych Regulaminem zastosowanie mają przepisy prawa polskiego.</p>
        <p>4. Spory wynikłe z korzystania z Serwisu będą rozstrzygane przez sąd właściwy dla siedziby Administratora.</p>
      </Section>

      <p className="text-xs text-muted-foreground mt-6">Kontakt: <a href="mailto:kontakt@znajdownik.pl" className="text-primary">kontakt@znajdownik.pl</a> | Wykonanie: <a href="https://www.zens.pl" className="text-primary" target="_blank" rel="noopener noreferrer">Zens IT</a></p>
    </div>
  );
}
